import { useCallback, useEffect, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { ViewSearch } from "#app/components/filters/view-search";
import { useAppNavigate } from "#app/lib/hooks/use-app-navigate";
import {
  Box,
  Button,
  DataTableToolbar,
  DataTableToolbarActions,
  Separator,
  Text,
} from "@salestrak/ui";
import { PageHeader } from "#app/components/page-header";
import {
  WORKFLOWS_DEFAULT_HIDDEN_COLUMN_IDS,
  WorkflowsTable,
  useWorkflowColumns,
} from "#app/components/workflows/workflows-table";
import { useTableViewSettings } from "#app/lib/hooks/use-table-view-settings";
import { DeleteWorkflowDialog } from "#app/components/workflows/delete-workflow-dialog";
import { CreateWorkflowDialog } from "#app/components/workflows/create-workflow-dialog";
import { useListQueryState } from "#app/lib/hooks/use-list-query-state";
import { WORKFLOW_LIST } from "#app/lib/filters/list-configs";
import type { WorkflowRow } from "#app/lib/data";
import { useAppData } from "#app/lib/data";
import { usePermission } from "#app/lib/hooks/use-permission";
import { withPermission } from "#app/components/permissions/require-permission";
import type { Omittable } from "@salestrak/ui";


function WorkflowsPage() {
  const { create } = Route.useSearch();
  const navigate = useNavigate();
  const { push } = useAppNavigate();

  const {
    searchValue,
    setSearchValue,
    sorting,
    setSorting,
    where,
    orderBy,
  } = useListQueryState(WORKFLOW_LIST);

  const { refetch: refetchAppData } = useAppData();
  const canCreateWorkflows = usePermission("create.workflows");

  // Dialog state
  const [createOpen, setCreateOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<WorkflowRow | null>(null);

  // Truly empty — zero workflows with no search narrowing the list. The
  // toolbar yields to the title row's create action; a zero-result search
  // keeps it so the viewer can widen the view again.
  const [workflowTotal, setWorkflowTotal] = useState<number | undefined>(undefined);
  const isTrulyEmpty = workflowTotal === 0 && !searchValue;

  // Open create dialog when navigated with ?create=true — but only with the
  // create permission, so a stale/forged link can't open the dialog. The
  // param is always cleared so it doesn't re-fire.
  useEffect(() => {
    if (create === "true") {
      if (canCreateWorkflows) setCreateOpen(true);
      navigate({ to: ".", search: (prev) => ({ ...prev, create: undefined }) });
    }
  }, [create, canCreateWorkflows, navigate]);

  // Handlers
  const navigateToWorkflow = useCallback(
    (workflowId: string) => {
      push(`/settings/workflows/${workflowId}`);
    },
    [push],
  );

  const handleCreated = useCallback(
    (workflowId: string) => {
      refetchAppData();
      navigateToWorkflow(workflowId);
    },
    [refetchAppData, navigateToWorkflow],
  );

  const openCreateDialog = useCallback(() => setCreateOpen(true), []);

  const clearDeleteTarget = useCallback(
    (open: boolean) => { if (!open) setDeleteTarget(null); },
    [],
  );

  const handleDeleted = useCallback(() => {
    setDeleteTarget(null);
    refetchAppData();
  }, [refetchAppData]);

  const openWorkflow = useCallback(
    (workflowId: string) => {
      // `/workflow/:id` has no view of its own — it redirects to the
      // workflow's default view.
      push(`/workflow/${workflowId}`);
    },
    [push],
  );

  const columns = useWorkflowColumns(navigateToWorkflow, openWorkflow);
  const tableView = useTableViewSettings("workflows", {
    defaultHiddenColumnIds: WORKFLOWS_DEFAULT_HIDDEN_COLUMN_IDS,
  });

  return (
    <Box direction="column" fill="flex">
      <PageHeader
        title="Workflows"
        actions={
          canCreateWorkflows ? (
            <Button size="xs" onClick={openCreateDialog}>
              <Text size={null}>Create Workflow</Text>
            </Button>
          ) : undefined
        }
      />
      <Separator />
      {/* The roster toolbar band shared with `/workflows`: search here,
          column show/hide on the actions-column circle-plus. */}
      {!isTrulyEmpty && (
        <DataTableToolbar>
          <DataTableToolbarActions>
            <ViewSearch
              onValueChange={setSearchValue}
              size="xs"
            />
          </DataTableToolbarActions>
        </DataTableToolbar>
      )}
      <WorkflowsTable
        where={where}
        orderBy={orderBy}
        sorting={sorting}
        onSortingChange={setSorting}
        searchValue={searchValue}
        columns={columns}
        tableViewProps={tableView.tableProps}
        onColumnVisibilityChange={
          tableView.settingsProps.onColumnVisibilityChange
        }
        onRequestDelete={setDeleteTarget}
        onTotalChange={setWorkflowTotal}
      />

      {canCreateWorkflows && (
        <CreateWorkflowDialog
          open={createOpen}
          onOpenChange={setCreateOpen}
          onCreated={handleCreated}
        />
      )}

      <DeleteWorkflowDialog
        workflow={deleteTarget}
        open={!!deleteTarget}
        onOpenChange={clearDeleteTarget}
        onDeleted={handleDeleted}
        onManageWorkflow={navigateToWorkflow}
      />
    </Box>
  );
}

export const Route = createFileRoute("/(app)/$workspace/settings/workflows/")({
  validateSearch: (
    search: Record<string, unknown>,
    // A search param is written back as an explicit `undefined` to clear it —
    // the router reads that as "absent from the URL" — so the members relax.
  ): Omittable<{ create?: string }> => ({
    create: typeof search.create === "string" ? search.create : undefined,
  }),
  component: withPermission("manage.edit_workflows", WorkflowsPage),
});
